import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import POS from "./pages/POS";
import Products from "./pages/Products";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Appointments from "./pages/Appointments";
import CalendarView from "./pages/CalendarView";
import Services from "./pages/Services";
import Suppliers from "./pages/Suppliers";
import Reports from "./pages/Reports";
import WhatsAppLogs from "./pages/WhatsAppLogs";
import Templates from "./pages/Templates";
import WhatsAppManage from "./pages/WhatsAppManage";
import Users from "./pages/Users";
import Branches from "./pages/Branches";
import Settings from "./pages/Settings";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import PublicBooking from "./pages/PublicBooking";
import PublicProduct from "./pages/PublicProduct";
import Catalog from "./pages/Catalog";
import Purchases from "./pages/Purchases";
import Expenses from "./pages/Expenses";
import Grooming from "./pages/Grooming";
import Boarding from "./pages/Boarding";
import PublicInvoice from "./pages/PublicInvoice";
import Marketing from "./pages/Marketing";
import Financing from "./pages/Financing";
import Banners from "./pages/Banners";
import StorefrontOrders from "./pages/StorefrontOrders";

// Storefront pages
import { StoreProvider } from "./storefront/StoreContext";
import { StorefrontLayout } from "./storefront/StorefrontLayout";
import StoreHome from "./storefront/Home";
import StoreProductList from "./storefront/ProductList";
import StoreProductDetail from "./storefront/ProductDetail";
import StoreCart from "./storefront/Cart";
import StoreCheckout from "./storefront/Checkout";
import StoreStores from "./storefront/Stores";
import StoreAccount from "./storefront/Account";

// Hostname detection: customer storefront on apex domain (furmigo.shop)
// admin/ERP stays on subdomain (store.furmigo.shop).
// For preview/local dev, ?storefront=1 query param forces storefront mode.
function isStorefrontHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  // Apex / www only (NOT store.furmigo.shop)
  if (h === "furmigo.shop" || h === "www.furmigo.shop") return true;
  // Manual override for preview
  if (window.location.search.includes("storefront=1")) {
    sessionStorage.setItem("furmigo_force_storefront", "1");
    return true;
  }
  if (sessionStorage.getItem("furmigo_force_storefront") === "1") return true;
  return false;
}

function StorefrontApp() {
  return (
    <StoreProvider>
      <StorefrontLayout>
        <Routes>
          <Route path="/" element={<StoreHome />} />
          <Route path="/products" element={<StoreProductList />} />
          <Route path="/products/:pid" element={<StoreProductDetail />} />
          <Route path="/cart" element={<StoreCart />} />
          <Route path="/checkout" element={<StoreCheckout />} />
          <Route path="/stores" element={<StoreStores />} />
          <Route path="/account" element={<StoreAccount />} />
          <Route path="/book" element={<PublicBooking />} />
          <Route path="/inv/:id" element={<PublicInvoice />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </StorefrontLayout>
    </StoreProvider>
  );
}

function AdminApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/book" element={<PublicBooking />} />
        <Route path="/shop/:id" element={<PublicProduct />} />
        <Route path="/inv/:id" element={<PublicInvoice />} />
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/pos" element={<ProtectedRoute roles={["admin", "manager", "billing"]}><POS /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute roles={["admin", "manager", "billing"]}><Invoices /></ProtectedRoute>} />
          <Route path="/invoices/:id" element={<ProtectedRoute roles={["admin", "manager", "billing"]}><InvoiceDetail /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute roles={["admin", "manager", "billing", "inventory"]}><Products /></ProtectedRoute>} />
          <Route path="/catalog" element={<ProtectedRoute roles={["admin", "manager", "inventory"]}><Catalog /></ProtectedRoute>} />
          <Route path="/purchases" element={<ProtectedRoute roles={["admin", "manager", "inventory"]}><Purchases /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute roles={["admin", "manager"]}><Expenses /></ProtectedRoute>} />
          <Route path="/financing" element={<ProtectedRoute roles={["admin", "manager"]}><Financing /></ProtectedRoute>} />
          <Route path="/marketing" element={<ProtectedRoute roles={["admin", "manager"]}><Marketing /></ProtectedRoute>} />
          <Route path="/banners" element={<ProtectedRoute roles={["admin", "manager"]}><Banners /></ProtectedRoute>} />
          <Route path="/storefront-orders" element={<ProtectedRoute roles={["admin", "manager"]}><StorefrontOrders /></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute roles={["admin", "manager", "billing", "grooming"]}><Customers /></ProtectedRoute>} />
          <Route path="/customers/:id" element={<ProtectedRoute roles={["admin", "manager", "billing", "grooming"]}><CustomerDetail /></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute roles={["admin", "manager", "billing", "grooming"]}><Appointments /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute roles={["admin", "manager", "billing", "grooming"]}><CalendarView /></ProtectedRoute>} />
          <Route path="/services" element={<ProtectedRoute roles={["admin", "manager"]}><Services /></ProtectedRoute>} />
          <Route path="/grooming" element={<ProtectedRoute roles={["admin", "manager", "grooming", "billing"]}><Grooming /></ProtectedRoute>} />
          <Route path="/boarding" element={<ProtectedRoute roles={["admin", "manager", "grooming", "billing"]}><Boarding /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute roles={["admin", "manager", "inventory"]}><Suppliers /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute roles={["admin", "manager"]}><Reports /></ProtectedRoute>} />
          <Route path="/whatsapp" element={<ProtectedRoute roles={["admin", "manager"]}><WhatsAppManage /></ProtectedRoute>} />
          <Route path="/whatsapp/legacy-logs" element={<ProtectedRoute roles={["admin", "manager"]}><WhatsAppLogs /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute roles={["admin", "manager"]}><Templates /></ProtectedRoute>} />
          <Route path="/settings/whatsapp" element={<ProtectedRoute roles={["admin", "manager"]}><WhatsAppManage /></ProtectedRoute>} />
          <Route path="/branches" element={<ProtectedRoute roles={["admin"]}><Branches /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute roles={["admin"]}><Settings /></ProtectedRoute>} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function App() {
  const storefront = isStorefrontHost();
  return (
    <div className="App">
      <BrowserRouter>
        {storefront ? <StorefrontApp /> : <AdminApp />}
      </BrowserRouter>
    </div>
  );
}

export default App;
